import { useState } from 'react'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  RadioGroup,
  RadioGroupItem,
  Textarea,
} from '@brent/core-ui/ui'
import { Loader2 } from 'lucide-react'
import type { Survey } from '@/services/api/surveys'

const LIKERT_LABELS = [
  { value: 1, label: 'Strongly Disagree' },
  { value: 2, label: 'Disagree' },
  { value: 3, label: 'Neutral' },
  { value: 4, label: 'Agree' },
  { value: 5, label: 'Strongly Agree' },
]

interface SurveyFormProps {
  survey: Survey
  currentAnswers?: {
    likert_answers: Record<string, number>
    free_text_answer: string
  }
  isPending: boolean
  onSubmit: (likertAnswers: Record<string, number>, freeTextAnswer: string) => void
}

export default function SurveyForm({
  survey,
  currentAnswers,
  isPending,
  onSubmit,
}: SurveyFormProps) {
  const [likertAnswers, setLikertAnswers] = useState<Record<string, number>>(
    currentAnswers?.likert_answers ?? {},
  )
  const [freeTextAnswer, setFreeTextAnswer] = useState(currentAnswers?.free_text_answer ?? '')

  const handleLikertChange = (questionId: string, value: string) => {
    setLikertAnswers((prev) => ({ ...prev, [questionId]: parseInt(value, 10) }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(likertAnswers, freeTextAnswer)
  }

  const allQuestionsAnswered = survey.likert_questions.every(
    (q) => likertAnswers[q.id] !== undefined,
  )

  const isUpdate = !!currentAnswers
  const closesAt = new Date(survey.closes_at)

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{survey.title}</CardTitle>
          {survey.description && <CardDescription>{survey.description}</CardDescription>}
          <CardDescription>
            {isUpdate
              ? `You have already responded. You can update your answers until ${closesAt.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}.`
              : `This survey closes on ${closesAt.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}.`}
          </CardDescription>
        </CardHeader>
      </Card>

      {survey.likert_questions.map((question, index) => (
        <Card key={question.id}>
          <CardHeader>
            <CardTitle className="text-base">
              {index + 1}. {question.text}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RadioGroup
              value={likertAnswers[question.id]?.toString() ?? ''}
              onValueChange={(value) => handleLikertChange(question.id, value)}
              className="space-y-2"
            >
              {LIKERT_LABELS.map((option) => (
                <div key={option.value} className="flex items-center space-x-3">
                  <RadioGroupItem
                    value={option.value.toString()}
                    id={`${question.id}-${option.value}`}
                  />
                  <Label
                    htmlFor={`${question.id}-${option.value}`}
                    className="cursor-pointer text-sm"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </CardContent>
        </Card>
      ))}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {survey.likert_questions.length + 1}. {survey.free_text_question}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={freeTextAnswer}
            onChange={(e) => setFreeTextAnswer(e.target.value)}
            placeholder="Type your response here..."
            rows={4}
            maxLength={5000}
          />
        </CardContent>
      </Card>

      <Button type="submit" disabled={!allQuestionsAnswered || isPending}>
        {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {isUpdate ? 'Update Response' : 'Submit Response'}
      </Button>
    </form>
  )
}
